import React from 'react';
import { MdRefresh } from 'react-icons/md';

interface LoadingComponentProps {
  loading: boolean;
  onRefresh?: () => void;
  message?: string;
  showRefreshButton?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

export default function LoadingComponent({ 
  loading, 
  onRefresh, 
  message = "Loading...", 
  showRefreshButton = true,
  size = 'medium',
  className = ""
}: LoadingComponentProps) {
  const sizeClasses = {
    small: 'h-6 w-6',
    medium: 'h-8 w-8', 
    large: 'h-12 w-12'
  };

  const textSizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg'
  };

  if (!loading) return null;

  return (
    <div className={`flex flex-col items-center justify-center py-8 ${className}`}>
      {/* Spinner */}
      <div className="relative">
        <div className={`animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 ${sizeClasses[size]}`}></div>
      </div>
      
      {/* Loading Message */}
      <p className={`mt-4 text-gray-600 ${textSizeClasses[size]}`}>
        {message}
      </p>
      
      {/* Refresh Button */}
      {showRefreshButton && onRefresh && (
        <button
          onClick={onRefresh}
          className="mt-4 flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
        >
          <MdRefresh className="h-4 w-4" />
          <span className="text-sm">Refresh</span>
        </button>
      )}
    </div>
  );
}

// Enhanced Spinner Component
export function EnhancedSpinner({ 
  size = 'medium', 
  color = 'blue',
  className = ""
}: {
  size?: 'small' | 'medium' | 'large';
  color?: 'blue' | 'green' | 'red' | 'gray';
  className?: string;
}) {
  const sizeClasses = {
    small: 'h-4 w-4',
    medium: 'h-6 w-6', 
    large: 'h-8 w-8'
  };

  const colorClasses = {
    blue: 'border-blue-600',
    green: 'border-green-600',
    red: 'border-red-600',
    gray: 'border-gray-600'
  };

  return (
    <div className={`animate-spin rounded-full border-2 border-gray-300 border-t-${colorClasses[color]} ${sizeClasses[size]} ${className}`}></div>
  );
}

// Loading Button Component
export function LoadingButton({ 
  loading, 
  children, 
  onClick,
  disabled = false,
  className = "",
  ...props
}: {
  loading: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
        loading || disabled
          ? 'bg-gray-400 text-gray-500 cursor-not-allowed'
          : 'bg-blue-500 hover:bg-blue-600 text-white'
      } ${className}`}
      {...props}
    >
      {loading && <EnhancedSpinner size="small" color="gray" />}
      <span>{children}</span>
    </button>
  );
}
